import { useTraderTime } from '../contexts/TraderTimeContext';
import { formatCardNumber } from '../lib/utils';
import { bankDisplayName } from '../lib/kgsBanks';
import { ResetOrderButton } from '../components/orders/ResetOrderButton';
import { OrderAutomation } from '../components/orders/OrderAutomation';
import React, { useState, useEffect } from 'react';
import { ordersAPI, OrderResponse, traderAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { Edit, Loader2, RefreshCw, X, TrendingUp } from 'lucide-react';
import { formatAmount as formatAmountUtil } from '../lib/utils';
import { CreateOrderForm } from '../components/orders/CreateOrderForm';
import { EditOrderDialog } from '../components/orders/EditOrderDialog';
import { Switch } from '../components/ui/switch';
import { TRADER_REQUISITES_REFRESH_INTERVAL_MS } from '../lib/polling';

export const DepositRequisitesPage: React.FC = () => {
    const { formatDate } = useTraderTime();
    const [fiatCurrency, setFiatCurrency] = useState<string | null>(null);
    useEffect(() => {
        let active = true;
        traderAPI.getSettings().then(settings => {
            if (active) setFiatCurrency(settings.fiat_currency || 'RUB');
        }).catch(() => { /* Keep the description currency-neutral until settings are available. */ });
        return () => { active = false; };
    }, []);
    const [orders, setOrders] = useState<OrderResponse[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize] = useState(50);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [cancellingOrder, setCancellingOrder] = useState<string | null>(null);
    const [updatingPauseOrder, setUpdatingPauseOrder] = useState<string | null>(null);
    const [editingOrder, setEditingOrder] = useState<OrderResponse | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const loadOrders = async (showLoading = true, page: number = currentPage) => {
        try {
            if (showLoading) {
                setIsLoading(true);
            }
            setError(null);
            const response = await ordersAPI.getMyOrders(page, pageSize);

            // Фильтруем только активные ордера на пополнение
            const activeDepositOrders = response.orders.filter(
                order => order.status === 'active' && order.order_type === 'deposit'
            );

            // Обновляем только если данные изменились
            setOrders(prevOrders => {
                const dataString = JSON.stringify(activeDepositOrders);
                const prevString = JSON.stringify(prevOrders);

                if (dataString === prevString) {
                    return prevOrders;
                }

                return activeDepositOrders;
            });

            setTotalPages(response.pages);
            setTotalItems(response.total);
            setCurrentPage(response.page);
        } catch (err: any) {
            setError('Не удалось загрузить реквизиты');
            console.error('Error loading orders:', err);
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        loadOrders(true, 1);
    }, [refreshTrigger]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        loadOrders(true, page);
    };

    // Автоматическое фоновое обновление накопленных сумм по реквизитам
    useEffect(() => {
        const interval = setInterval(() => {
            loadOrders(false); // Фоновое обновление без индикатора
        }, TRADER_REQUISITES_REFRESH_INTERVAL_MS);

        return () => clearInterval(interval);
    }, []);

    const handleCancelOrder = async (orderId: string) => {
        try {
            setCancellingOrder(orderId);
            await ordersAPI.cancelOrder(orderId);
            await loadOrders();
        } catch (err: any) {
            setError('Не удалось отменить ордер');
        } finally {
            setCancellingOrder(null);
        }
    };

    const handleTogglePause = async (order: OrderResponse) => {
        try {
            setUpdatingPauseOrder(order.id);
            await ordersAPI.updateOrder(order.id, { is_paused: !order.is_paused });
            await loadOrders(false);
        } catch (err: any) {
            setError('Не удалось обновить паузу');
        } finally {
            setUpdatingPauseOrder(null);
        }
    };

    const handleOrderCreated = () => {
        setRefreshTrigger(prev => prev + 1);
    };


    const formatAmount = (amount: number, currency?: string) => {
        return formatAmountUtil(amount, currency);
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            active: { label: 'Активный', variant: 'default' as const },
            completed: { label: 'Завершен', variant: 'secondary' as const },
            cancelled: { label: 'Отменен', variant: 'destructive' as const },
        };

        const config = statusConfig[status as keyof typeof statusConfig] || {
            label: status,
            variant: 'secondary' as const,
        };

        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const renderOrderCard = (order: OrderResponse) => (
        <div
            key={order.id}
            className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${order.is_paused ? 'border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/40' : 'border-gray-200 dark:border-slate-700'}`}
        >
            <div className="flex flex-wrap items-start gap-4">
                <div className="min-w-0 flex-[3_1_28rem] break-words">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-300" />
                        <span className="font-medium">Пополнение</span>
                        {getStatusBadge(order.status)}
                        {order.is_paused && (
                            <Badge variant="secondary" className="bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-200">
                                На паузе
                            </Badge>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm [&>div]:min-w-0">
                        <div>
                            <p className="text-gray-600 dark:text-slate-300">Объём ордера:</p>
                            <p className="font-medium">
                                {formatAmount(order.from_amount)} {order.from_currency}
                            </p>
                            <p className="text-gray-600 dark:text-slate-300">
                                Остаток: {formatAmount(order.remaining_fiat_amount ?? Math.max(0, order.from_amount - Math.max(order.reserved_amount || 0, order.accumulated_amount || 0)))} {order.from_currency}
                            </p>

                        </div>

                        <div>
                            <p className="text-gray-600 dark:text-slate-300">Создан:</p>
                            <p>{formatDate(order.created_at)}</p>
                        </div>

                        <div>
                            <p className="text-gray-600 dark:text-slate-300">ID:</p>
                            <p className="font-mono text-xs break-all">{order.id}</p>
                        </div>

                        {/* Реквизиты */}
                        {order.phone_number && (
                            <div>
                                <p className="text-gray-600 dark:text-slate-300">Телефон:</p>
                                <p className="font-medium">{order.phone_number}</p>
                            </div>
                        )}

                        {order.recipient_bank && (
                            <div>
                                <p className="text-gray-600 dark:text-slate-300">Банк:</p>
                                <p className="font-medium">{bankDisplayName(order.recipient_bank)}</p>
                            </div>
                        )}

                        {order.recipient_name && (
                            <div>
                                <p className="text-gray-600 dark:text-slate-300">Получатель:</p>
                                <p className="font-medium">{order.recipient_name}</p>
                            </div>
                        )}

                        {order.recipient_card && (
                            <div className="sm:col-span-2">
                                <p className="text-gray-600 dark:text-slate-300">Номер карты:</p>
                                <p className="font-medium font-mono">{formatCardNumber(order.recipient_card)}</p>
                            </div>
                        )}

                        {order.notes && (
                            <div className="sm:col-span-2">
                                <p className="text-gray-600 dark:text-slate-300">Примечание:</p>
                                <p className="font-medium text-sm bg-gray-50 dark:bg-slate-900 p-2 rounded border-l-4 border-blue-500">
                                    {order.notes}
                                </p>
                            </div>
                        )}

                        {/* Прогресс заполнения */}
                        {order.accumulated_amount !== undefined && (
                            <div className="sm:col-span-2">
                                <p className="text-gray-600 dark:text-slate-300">Прогресс заполнения:</p>
                                <div className="mt-2 space-y-2">
                                    <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-sm">
                                        <span className="text-gray-600 dark:text-slate-300">
                                            Накоплено: {formatAmount(order.accumulated_amount)} из {formatAmount(order.from_amount)} {order.from_currency}
                                        </span>
                                        <span className="font-medium">
                                            {formatAmountUtil(Math.min((order.accumulated_amount / order.from_amount) * 100, 100))}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full transition-all duration-300 ${order.accumulated_amount >= order.from_amount
                                                ? 'bg-green-600'
                                                : 'bg-blue-600'
                                                }`}
                                            style={{ width: `${Math.min((order.accumulated_amount / order.from_amount) * 100, 100)}%` }}
                                        ></div>
                                    </div>
                                    {order.accumulated_amount >= order.from_amount && (
                                        <div className="text-green-600 dark:text-green-300 text-sm font-medium">
                                            ✓ Ордер полностью заполнен
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {order.status === 'active' && (
                    <div className="flex min-w-0 flex-[1_1_16rem] flex-col items-end gap-2">
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300">
                            <span>{order.is_paused ? 'На паузе' : 'Прием заявок'}</span>
                            <Switch
                                checked={!order.is_paused}
                                onCheckedChange={() => handleTogglePause(order)}
                                disabled={updatingPauseOrder === order.id}
                            />
                        </div>
                        <OrderAutomation order={order} onUpdated={() => loadOrders(false)} />
                        <ResetOrderButton order={order}
                            onReset={updated => {
                                setError(null);
                                setOrders(items => items.map(item => item.id === updated.id ? updated : item));
                            }}
                            onError={setError}
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingOrder(order)}
                        >
                            <Edit className="h-4 w-4" />
                            Редактировать
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelOrder(order.id)}
                            disabled={cancellingOrder === order.id}
                        >
                            {cancellingOrder === order.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <X className="h-4 w-4" />
                            )}
                            Отменить
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Реквизиты для пополнения</h1>
                <p className="text-gray-600 dark:text-slate-300 mt-2">
                    Управляйте реквизитами для приёма {fiatCurrency ? `${fiatCurrency} ` : ''}от клиентов
                </p>
            </div>

            {error && (
                <div className="p-4 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                    {error}
                </div>
            )}

            {/* Контент */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Форма создания ордера на пополнение */}
                <CreateOrderForm orderType="deposit" onOrderCreated={handleOrderCreated} />

                {/* Список активных реквизитов */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle>Рабочие реквизиты</CardTitle>
                            <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
                                Активные реквизиты для приёма платежей
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadOrders(true)}
                            disabled={isLoading}
                        >
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-8 w-8 animate-spin" />
                                <span className="ml-2">Загрузка...</span>
                            </div>
                        ) : orders.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                                <p>Нет активных реквизитов</p>
                                <p className="text-sm mt-1">
                                    Создайте ордер на пополнение
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {orders.map(order => renderOrderCard(order))}

                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                    totalItems={totalItems}
                                    pageSize={pageSize}
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
            <EditOrderDialog
                order={editingOrder}
                open={Boolean(editingOrder)}
                onOpenChange={(open) => {
                    if (!open) setEditingOrder(null);
                }}
                onSaved={() => loadOrders(true)}
            />
        </div>
    );
};
